'use client'

import InputError from '@/components/InputError'
import { useAuth } from '@/hooks/auth'
import { useEffect, useState } from 'react'
import { useRouter } from 'next/navigation'
import AuthSessionStatus from '@/app/(auth)/AuthSessionStatus'
import { Eye, EyeOff } from 'lucide-react'
import useAxiosInstance from '@/lib/axios'

const Login = () => {
    const router = useRouter()
    const axios = useAxiosInstance()

    const {
        tokenBasedlogin: signin,
        tokenBasedPasskeyLogin: signinWithPasskey,
        requestEmailOtp,
        loginWithEmailOtp,
        isSigning,
    } = useAuth({
        middleware: 'guest',
        redirectIfAuthenticated: '/dashboard',
    })

    const [authMode, setAuthMode] = useState('password')
    const [login, setLogin] = useState('')
    const [password, setPassword] = useState('')
    const [otp, setOtp] = useState('')
    const [isSendingOtp, setIsSendingOtp] = useState(false)
    const [showPassword, setShowPassword] = useState(false)
    const [shouldRemember, setShouldRemember] = useState(false)
    const [mfaCode, setMfaCode] = useState('')
    const [recoveryCode, setRecoveryCode] = useState('')
    const [mfaStep, setMfaStep] = useState(false)
    const [availableMfaMethods, setAvailableMfaMethods] = useState([])
    const [selectedMfaMethod, setSelectedMfaMethod] = useState('totp')
    const [securitySettings, setSecuritySettings] = useState({
        allow_webauthn_login: false,
        allow_email_otp_login: false,
    })
    const [errors, setErrors] = useState([])
    const [status, setStatus] = useState(null)

    const canUsePasskey = securitySettings.allow_webauthn_login !== false
    const canUseEmailOtp = securitySettings.allow_email_otp_login !== false

    const normalizeMethod = method => {
        if (method === 'recovery_code') {
            return 'recovery'
        }
        if (method === 'passkey') {
            return 'passkey'
        }
        return 'totp'
    }

    const methodLabel = method => {
        if (method === 'passkey') {
            return 'Passkey'
        }
        if (method === 'recovery') {
            return 'Recovery Code'
        }
        return 'Authenticator App'
    }

    const chooseDefaultMethod = methods => {
        if (methods.includes('passkey')) {
            return 'passkey'
        }
        if (methods.includes('totp')) {
            return 'totp'
        }
        if (methods.includes('recovery')) {
            return 'recovery'
        }
        return 'totp'
    }

    useEffect(() => {
        if (router.reset?.length > 0 && errors.length === 0) {
            setStatus(atob(router.reset))
        } else {
            setStatus(null)
        }
    })

    useEffect(() => {
        const loadSecuritySettings = async () => {
            try {
                const response = await axios.get('/api/auth/security-settings')
                setSecuritySettings(response.data || {})
            } catch {
                // Keep login-method toggles hidden until the backend confirms they are enabled.
            }
        }

        loadSecuritySettings()
    }, [axios])

    useEffect(() => {
        if (authMode === 'email_otp' && !canUseEmailOtp) {
            setAuthMode('password')
        }
    }, [authMode, canUseEmailOtp])

    const submitForm = async event => {
        event.preventDefault()
        const wasInMfaStep = mfaStep

        if (authMode === 'email_otp' && !canUseEmailOtp) {
            setErrors(
                'Email OTP login is currently disabled by an administrator.',
            )
            return
        }

        const result =
            authMode === 'email_otp'
                ? await loginWithEmailOtp({
                      email: login,
                      otp,
                      mfa_code: selectedMfaMethod === 'totp' ? mfaCode : '',
                      recovery_code:
                          selectedMfaMethod === 'recovery' ? recoveryCode : '',
                      setErrors,
                      setStatus,
                  })
                : await signin({
                      login,
                      password,
                      mfa_code: selectedMfaMethod === 'totp' ? mfaCode : '',
                      recovery_code:
                          selectedMfaMethod === 'recovery' ? recoveryCode : '',
                      remember: shouldRemember,
                      setErrors,
                      setStatus,
                  })

        if (result?.mfaRequired) {
            const methods = (result.methods || []).map(normalizeMethod)
            const uniqueMethods = [...new Set(methods)]
            setMfaStep(true)
            setAvailableMfaMethods(uniqueMethods)

            if (!wasInMfaStep || !uniqueMethods.includes(selectedMfaMethod)) {
                setSelectedMfaMethod(chooseDefaultMethod(uniqueMethods))
            }

            if (wasInMfaStep) {
                setErrors(
                    result.message ||
                        'Verification failed. Please check your code and try again.',
                )
                setStatus(null)
            } else {
                setErrors([])
                setStatus(
                    'Multi-factor verification required. Choose one method to continue.',
                )
            }
            return
        }

        if (result?.ok) {
            setMfaStep(false)
            setAvailableMfaMethods([])
            setSelectedMfaMethod('totp')
            setMfaCode('')
            setRecoveryCode('')
        }
    }

    const handlePasskeyLogin = async () => {
        const inputEl =
            typeof document !== 'undefined'
                ? document.getElementById('login')
                : null
        const resolvedLogin = (login || inputEl?.value || '').toString().trim()

        if (!resolvedLogin) {
            setErrors('Enter your email or username first, then try passkey.')
            return
        }

        if (!canUsePasskey) {
            setErrors(
                'Passkey login is currently disabled by an administrator.',
            )
            return
        }

        await signinWithPasskey({
            login: resolvedLogin,
            setErrors,
            setStatus,
        })
    }

    const handleSendOtp = async () => {
        const email = (login || '').trim()
        if (!email) {
            setErrors('Enter your email first.')
            return
        }

        setIsSendingOtp(true)
        await requestEmailOtp({
            email,
            setErrors,
            setStatus,
        })
        setIsSendingOtp(false)
    }

    const onMethodChange = method => {
        setSelectedMfaMethod(method)
        setErrors([])
        setStatus(null)
        if (method !== 'totp') {
            setMfaCode('')
        }
        if (method !== 'recovery') {
            setRecoveryCode('')
        }
    }

    return (
        <>
            <AuthSessionStatus
                className="mb-4 text-white/70 text-sm text-center"
                status={status}
            />
            <form onSubmit={submitForm} className="flex flex-col gap-5">
                <div
                    className={`grid gap-2 ${
                        canUseEmailOtp ? 'grid-cols-2' : 'grid-cols-1'
                    }`}>
                    <button
                        type="button"
                        onClick={() => {
                            setAuthMode('password')
                            setErrors([])
                            setStatus(null)
                        }}
                        className={`rounded-xl px-3 py-2 text-xs font-semibold tracking-wide border transition-all ${
                            authMode === 'password'
                                ? 'bg-white/25 border-white/50 text-white'
                                : 'bg-white/5 border-white/20 text-white/80 hover:bg-white/10'
                        }`}>
                        Password Login
                    </button>
                    {canUseEmailOtp && (
                        <button
                            type="button"
                            onClick={() => {
                                setAuthMode('email_otp')
                                setPassword('')
                                setErrors([])
                                setStatus(null)
                            }}
                            className={`rounded-xl px-3 py-2 text-xs font-semibold tracking-wide border transition-all ${
                                authMode === 'email_otp'
                                    ? 'bg-white/25 border-white/50 text-white'
                                    : 'bg-white/5 border-white/20 text-white/80 hover:bg-white/10'
                            }`}>
                            Email OTP Login
                        </button>
                    )}
                </div>

                {!canUseEmailOtp && (
                    <p className="text-[11px] text-white/60 -mt-2">
                        Email OTP login is disabled by your administrator.
                    </p>
                )}

                {/* Email / Username */}
                <div className="flex flex-col gap-1.5">
                    <label
                        htmlFor="login"
                        className="text-white/70 text-xs font-semibold uppercase tracking-widest">
                        {authMode === 'email_otp'
                            ? 'Email'
                            : 'Email or Username'}
                    </label>
                    <input
                        id="login"
                        type={authMode === 'email_otp' ? 'email' : 'text'}
                        value={login}
                        onChange={event => setLogin(event.target.value)}
                        required
                        autoFocus
                        placeholder="you@example.com"
                        className="w-full bg-white/10 border border-white/20 text-white placeholder-white/30 rounded-xl px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-white/30 focus:border-white/40 transition-all duration-200"
                    />
                    <InputError
                        messages={errors.login}
                        className="text-red-300 text-xs mt-0.5"
                    />
                </div>

                {authMode === 'password' ? (
                    <div className="flex flex-col gap-1.5">
                        <label
                            htmlFor="password"
                            className="text-white/70 text-xs font-semibold uppercase tracking-widest">
                            Password
                        </label>
                        <div className="relative">
                            <input
                                id="password"
                                type={showPassword ? 'text' : 'password'}
                                value={password}
                                onChange={event =>
                                    setPassword(event.target.value)
                                }
                                required
                                autoComplete="current-password"
                                placeholder="••••••••"
                                className="w-full bg-white/10 border border-white/20 text-white placeholder-white/30 rounded-xl px-4 pr-11 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-white/30 focus:border-white/40 transition-all duration-200"
                            />
                            <button
                                type="button"
                                onClick={() => setShowPassword(v => !v)}
                                aria-label={
                                    showPassword
                                        ? 'Hide password'
                                        : 'Show password'
                                }
                                className="absolute inset-y-0 right-0 px-3 text-white/60 hover:text-white transition-colors">
                                {showPassword ? (
                                    <EyeOff className="w-4 h-4" />
                                ) : (
                                    <Eye className="w-4 h-4" />
                                )}
                            </button>
                        </div>
                        <InputError
                            messages={errors.password}
                            className="text-red-300 text-xs mt-0.5"
                        />
                    </div>
                ) : canUseEmailOtp ? (
                    <div className="flex flex-col gap-2 rounded-xl border border-white/20 bg-white/5 p-3">
                        <div className="flex items-center gap-2">
                            <input
                                id="otp"
                                type="text"
                                value={otp}
                                onChange={event => setOtp(event.target.value)}
                                required
                                maxLength={6}
                                placeholder="Enter 6-digit OTP"
                                className="w-full bg-white/10 border border-white/20 text-white placeholder-white/30 rounded-xl px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-white/30 focus:border-white/40 transition-all duration-200"
                            />
                            <button
                                type="button"
                                onClick={handleSendOtp}
                                disabled={isSendingOtp || isSigning}
                                className="shrink-0 rounded-xl px-3 py-3 text-xs font-semibold tracking-wide border border-white/30 bg-white/10 text-white hover:bg-white/20 disabled:opacity-60 disabled:cursor-not-allowed">
                                {isSendingOtp ? 'Sending...' : 'Send OTP'}
                            </button>
                        </div>
                        <p className="text-[11px] text-white/70">
                            We will send a one-time login code to your email.
                        </p>
                    </div>
                ) : null}

                {/* Generic error */}
                {errors && typeof errors === 'string' && (
                    <p className="text-red-300 text-sm text-center">{errors}</p>
                )}

                {mfaStep && (
                    <div className="space-y-3 rounded-xl border border-white/20 bg-white/5 p-4">
                        <p className="text-white/80 text-sm font-medium">
                            Choose one verification method
                        </p>

                        <div className="flex flex-col gap-2">
                            {availableMfaMethods.map(method => (
                                <button
                                    key={method}
                                    type="button"
                                    onClick={() => onMethodChange(method)}
                                    className={`rounded-lg px-3 py-2 text-xs font-semibold tracking-wide border transition-all ${
                                        selectedMfaMethod === method
                                            ? 'bg-white/25 border-white/50 text-white'
                                            : 'bg-white/5 border-white/20 text-white/80 hover:bg-white/10'
                                    }`}>
                                    {methodLabel(method)}
                                </button>
                            ))}
                        </div>

                        {selectedMfaMethod === 'totp' && (
                            <div className="flex flex-col gap-1.5">
                                <label
                                    htmlFor="mfa_code"
                                    className="text-white/70 text-xs font-semibold uppercase tracking-widest">
                                    Authenticator Code
                                </label>
                                <input
                                    id="mfa_code"
                                    type="text"
                                    value={mfaCode}
                                    onChange={event => {
                                        setMfaCode(event.target.value)
                                        if (typeof errors === 'string') {
                                            setErrors([])
                                        }
                                    }}
                                    maxLength={6}
                                    placeholder="123456"
                                    className="w-full bg-white/10 border border-white/20 text-white placeholder-white/30 rounded-xl px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-white/30 focus:border-white/40 transition-all duration-200"
                                />
                            </div>
                        )}

                        {selectedMfaMethod === 'recovery' && (
                            <div className="flex flex-col gap-1.5">
                                <label
                                    htmlFor="recovery_code"
                                    className="text-white/70 text-xs font-semibold uppercase tracking-widest">
                                    Recovery Code
                                </label>
                                <input
                                    id="recovery_code"
                                    type="text"
                                    value={recoveryCode}
                                    onChange={event => {
                                        setRecoveryCode(event.target.value)
                                        if (typeof errors === 'string') {
                                            setErrors([])
                                        }
                                    }}
                                    placeholder="ABCDE-12345"
                                    className="w-full bg-white/10 border border-white/20 text-white placeholder-white/30 rounded-xl px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-white/30 focus:border-white/40 transition-all duration-200"
                                />
                            </div>
                        )}

                        {selectedMfaMethod === 'passkey' && (
                            <button
                                type="button"
                                onClick={handlePasskeyLogin}
                                disabled={isSigning}
                                className="w-full py-3 rounded-xl bg-emerald-500/20 hover:bg-emerald-500/30 border border-emerald-300/30 text-white font-semibold text-sm tracking-wide transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-emerald-300/40 disabled:opacity-50 disabled:cursor-not-allowed">
                                Continue with Passkey
                            </button>
                        )}
                    </div>
                )}

                {authMode === 'password' && (
                    <div className="flex items-center gap-2">
                        <input
                            id="remember_me"
                            type="checkbox"
                            name="remember"
                            onChange={event =>
                                setShouldRemember(event.target.checked)
                            }
                            className="w-4 h-4 rounded border-white/30 bg-white/10 text-blue-400 focus:ring-white/20"
                        />
                        <label
                            htmlFor="remember_me"
                            className="text-white/60 text-sm cursor-pointer select-none">
                            Remember me
                        </label>
                    </div>
                )}

                {/* Submit */}
                <button
                    type="submit"
                    disabled={isSigning}
                    className="w-full mt-1 py-3 rounded-xl bg-white/20 hover:bg-white/30 border border-white/25 text-white font-semibold text-sm tracking-wide transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-white/40 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2">
                    {isSigning ? (
                        <>
                            <svg
                                className="animate-spin h-4 w-4 text-white"
                                fill="none"
                                viewBox="0 0 24 24">
                                <circle
                                    className="opacity-25"
                                    cx="12"
                                    cy="12"
                                    r="10"
                                    stroke="currentColor"
                                    strokeWidth="4"
                                />
                                <path
                                    className="opacity-75"
                                    fill="currentColor"
                                    d="M4 12a8 8 0 018-8v8z"
                                />
                            </svg>
                            Signing in…
                        </>
                    ) : mfaStep ? (
                        selectedMfaMethod === 'recovery' ? (
                            'Verify with Recovery Code'
                        ) : selectedMfaMethod === 'totp' ? (
                            'Verify Authenticator Code'
                        ) : (
                            'Continue'
                        )
                    ) : authMode === 'email_otp' ? (
                        'Sign In with OTP'
                    ) : (
                        'Sign In'
                    )}
                </button>

                {!mfaStep && authMode === 'password' && canUsePasskey && (
                    <button
                        type="button"
                        onClick={handlePasskeyLogin}
                        disabled={isSigning}
                        className="w-full py-3 rounded-xl bg-emerald-500/20 hover:bg-emerald-500/30 border border-emerald-300/30 text-white font-semibold text-sm tracking-wide transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-emerald-300/40 disabled:opacity-50 disabled:cursor-not-allowed">
                        Sign In with Passkey (Fingerprint / Device PIN)
                    </button>
                )}
            </form>
        </>
    )
}

export default Login
